import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ScorePill, RiskPill, ConfidencePill, ROICell } from "@/components/opportunities/ScorePill";
import { ArrowLeft, BookmarkSimple, Plus, ArrowSquareOut, ArrowsClockwise, PencilSimple, Check, X, WarningCircle, Clock } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { amazonUrl, bricklinkUrl, bricklinkPriceGuideUrl } from "@/lib/links";
import SetThumb from "@/components/opportunities/SetThumb";
import DataSourcePill from "@/components/opportunities/DataSourcePill";
import AsinStatusPill from "@/components/opportunities/AsinStatusPill";
import { ManualPriceDialog } from "@/components/deals/ManualPriceDialog";
import { Copy, Receipt } from "@phosphor-icons/react";

const money = (n) => (n == null ? "--" : `$${Number(n).toFixed(2)}`);

export default function SetDetail() {
  const { setNumber } = useParams();
  const [data, setData] = useState(null);
  const [watchlists, setWatchlists] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [editAsin, setEditAsin] = useState(false);
  const [asinDraft, setAsinDraft] = useState("");
  const [showDemoData, setShowDemoData] = useState(false);  // for not-on-Amazon override
  const [manualState, setManualState] = useState({ open: false, field: null, setNumber: null, currentValue: null, isManual: false, currentNote: null });
  const openManualPrice = (field, currentValue, isManual, currentNote) =>
    setManualState({ open: true, field, setNumber, currentValue, isManual, currentNote });

  const reload = () => api.getOpportunity(setNumber).then(setData).catch(() => toast.error("Failed to load set"));

  useEffect(() => {
    reload();
    api.listWatchlists().then(setWatchlists).catch(() => {});
    // eslint-disable-next-line
  }, [setNumber]);

  const liveRefresh = async () => {
    setRefreshing(true);
    try {
      const d = await api.liveLookup(setNumber);
      setData(d);
      toast.success("Live data refreshed from Keepa + BrickLink");
    } catch (e) {
      toast.error("Live fetch failed — check API keys / connectivity");
    }
    setRefreshing(false);
  };

  const saveAsin = async () => {
    const a = asinDraft.trim().toUpperCase();
    if (a.length < 8) { toast.error("ASIN looks invalid"); return; }
    setRefreshing(true);
    try {
      const d = await api.overrideAsin(setNumber, a);
      setData(d);
      setEditAsin(false);
      toast.success(`ASIN updated to ${a} and live-refreshed`);
    } catch {
      toast.error("ASIN override failed");
    }
    setRefreshing(false);
  };

  if (!data) return <div className="p-8 text-gray-500 font-mono text-sm">Loading…</div>;
  const o = data.opportunity;
  const s = data.set || {};
  const amz = o.amazon || {};
  const bl = o.bricklink || {};
  const notOnAmazon = o.amazon_listed === false;
  // Manual overrides live on the catalog row — they win over scanned values (same as Deals).
  const mAmz = s.manual_amz_buy_box, mBl = s.manual_bl_sealed, mBlAvg = s.manual_bl_avg;
  const buyBox = mAmz ?? amz.buy_box;
  const blLowest = mBl ?? bl.lowest_sealed_us;
  const blAvg = mBlAvg ?? bl.avg_sealed;
  const amzHref = amazonUrl(o.asin, o.set_number, o.asin_verified);
  const blHref = bricklinkUrl(o.set_number);
  const copyAsin = () => { if (o.asin) { navigator.clipboard?.writeText(o.asin); toast.success(`ASIN ${o.asin} copied`); } };

  // "Last live" age — formatted as relative time + tone (fresh / stale / cold).
  const lastLive = o.last_live_at ? new Date(o.last_live_at) : null;
  const lastLiveMs = lastLive ? Date.now() - lastLive.getTime() : null;
  const lastLiveLabel = formatAge(lastLiveMs);
  const lastLiveTone = ageTone(lastLiveMs);

  const priceHist = (amz.price_history || []).map((v, i) => ({
    i, amazon: v, bricklink: (bl.price_history || [])[i] || null,
  }));

  const addToWl = async (wlId) => {
    try {
      await api.addToWatchlist(wlId, setNumber);
      toast.success("Added to watchlist");
      setPickerOpen(false);
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="p-6 space-y-6">
      <Link to="/" className="inline-flex items-center gap-2 text-xs font-mono text-gray-600 hover:text-[#E3000B]" data-testid="back-link">
        <ArrowLeft size={12} /> BACK TO BOARD
      </Link>

      <div className="flex flex-col md:flex-row gap-6 items-start">
        <div className="w-32 h-32 bg-[#F6F6F3] border border-[#E4E4E0] rounded-sm flex items-center justify-center flex-shrink-0">
          <SetThumb src={s.image_url} alt={s.name} size={120} className="border-0" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-xs text-[#E3000B]">#{o.set_number}</span>
            {o.retired && <span className="text-[10px] px-1.5 py-0.5 border border-zinc-600 text-gray-600 rounded-sm font-mono">RETIRED</span>}
            <AsinStatusPill row={o} testId="detail-asin-pill" />
            {o.asin && (
              <button onClick={copyAsin} title="Copy ASIN" data-testid="detail-asin-copy"
                className="text-gray-500 hover:text-[#E3000B]"><Copy size={12} /></button>
            )}
          </div>
          <h1 className="font-head text-3xl font-black tracking-tighter uppercase mb-1" data-testid="set-detail-name">{o.name}</h1>
          <div className="font-mono text-sm text-gray-600 mb-4">{o.theme}{s.subtheme ? ` · ${s.subtheme}` : ""} · {s.year} · {s.pieces} pcs</div>
          <div className="flex flex-wrap gap-3 items-center">
            <ScorePill score={o.opportunity_score} testId="detail-score" />
            <RiskPill risk={o.risk_rating} testId="detail-risk" />
            <ConfidencePill confidence={o.confidence} testId="detail-confidence" />
            <DataSourcePill source={o.data_source} testId="detail-data-source" />
            {lastLiveLabel && (
              <span
                data-testid="detail-last-live"
                title={lastLive ? `Live data fetched at ${lastLive.toLocaleString()}` : ""}
                className={`inline-flex items-center gap-1 px-2 h-6 border rounded-sm font-mono text-[10px] uppercase tracking-wider ${
                  lastLiveTone === "fresh" ? "border-[#00A850]/40 bg-[#00A850]/10 text-[#00A850]" :
                  lastLiveTone === "warn"  ? "border-[#E3000B]/40 bg-[#E3000B]/10 text-[#E3000B]" :
                                              "border-[#FF8C00]/40 bg-[#FF8C00]/10 text-[#FF8C00]"
                }`}
              >
                <Clock size={10} weight="bold" /> Live: {lastLiveLabel}
              </span>
            )}
            {!lastLiveLabel && o.data_source !== "live" && (
              <span data-testid="detail-never-live"
                className="inline-flex items-center gap-1 px-2 h-6 border border-gray-300 bg-gray-100 rounded-sm font-mono text-[10px] uppercase tracking-wider text-gray-500"
                title="No live fetch on record — Live Refresh recommended">
                <Clock size={10} weight="bold" /> Never live
              </span>
            )}
            <Button onClick={liveRefresh} disabled={refreshing} className="h-8 rounded-sm bg-[#E3000B] text-white hover:bg-[#E6B800] font-bold uppercase tracking-wider text-xs disabled:opacity-50" data-testid="live-refresh-btn">
              <ArrowsClockwise size={12} className={`mr-1 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "FETCHING…" : "LIVE REFRESH"}
            </Button>
            <a href={amazonUrl(o.asin, o.set_number, o.asin_verified)} target="_blank" rel="noopener noreferrer"
               data-testid="detail-amazon-link"
               title={o.asin_verified ? `Verified Amazon listing (ASIN ${o.asin})` : "Searches Amazon — set ASIN below for direct link"}
               className={`inline-flex items-center gap-1.5 h-8 px-3 border rounded-sm bg-[#F6F6F3] text-xs font-bold uppercase tracking-wider transition-colors ${
                 o.asin_verified
                   ? "border-[#D97706] text-[#D97706] hover:bg-[#D97706]/10"
                   : "border-[#E4E4E0] text-[#D97706] hover:border-[#D97706] hover:bg-[#D97706]/10"
               }`}>
              {o.asin_verified ? "VIEW LISTING" : "SEARCH AMAZON"} <ArrowSquareOut size={12} />
            </a>
            <a href={bricklinkPriceGuideUrl(o.set_number)} target="_blank" rel="noopener noreferrer"
               data-testid="detail-bricklink-link"
               className="inline-flex items-center gap-1.5 h-8 px-3 border border-[#E4E4E0] rounded-sm bg-[#F6F6F3] text-xs font-bold uppercase tracking-wider text-[#0055BF] hover:border-[#0055BF] hover:bg-[#0055BF]/10 transition-colors">
              VIEW ON BRICKLINK <ArrowSquareOut size={12} />
            </a>
            <Button onClick={() => setPickerOpen(true)} variant="outline" className="h-8 rounded-sm border-[#E4E4E0] bg-[#F6F6F3] text-xs font-mono hover:border-[#E3000B] hover:text-[#E3000B]" data-testid="add-to-watchlist">
              <BookmarkSimple size={12} className="mr-1" /> WATCHLIST
            </Button>
            <Link to="/deals/table" data-testid="detail-deals-link"
              className="inline-flex items-center gap-1.5 h-8 px-3 border border-[#E4E4E0] rounded-sm bg-[#F6F6F3] text-xs font-mono hover:border-[#00A850] hover:text-[#00A850] transition-colors">
              <Receipt size={12} /> DEALS
            </Link>
          </div>

          {/* ASIN editor */}
          <div className="mt-3 flex items-center gap-2 font-mono text-[11px]">
            <span className="text-gray-500 uppercase">ASIN</span>
            {!editAsin ? (
              <>
                <span className="text-gray-800" data-testid="detail-asin-value">{o.asin || "— not set —"}</span>
                <button onClick={() => { setAsinDraft(o.asin || ""); setEditAsin(true); }}
                  className="text-gray-500 hover:text-[#E3000B]" data-testid="detail-asin-edit"
                  title="Override Amazon ASIN to fix incorrect mapping">
                  <PencilSimple size={11} />
                </button>
                {o.asin && (
                  <span className="text-gray-400 text-[10px]">
                    (used by Amazon search; click pencil to correct)
                  </span>
                )}
              </>
            ) : (
              <>
                <Input value={asinDraft} onChange={e => setAsinDraft(e.target.value)}
                  placeholder="B0XXXXXXXX" maxLength={10}
                  className="h-7 w-32 bg-[#F6F6F3] border-[#E4E4E0] font-mono text-xs px-2"
                  data-testid="detail-asin-input" />
                <button onClick={saveAsin} disabled={refreshing}
                  className="text-[#00A850] hover:text-[#00CC33]" data-testid="detail-asin-save">
                  <Check size={12} />
                </button>
                <button onClick={() => setEditAsin(false)} className="text-gray-500 hover:text-[#FF3333]" data-testid="detail-asin-cancel">
                  <X size={12} />
                </button>
                <span className="text-gray-400 text-[10px]">
                  Tip: copy the 10-char ASIN from the Amazon URL after /dp/
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Not-on-Amazon banner */}
      {notOnAmazon && (
        <div className="border border-[#FF3333]/50 bg-[#FF3333]/[0.06] rounded-sm p-4 flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid="not-on-amazon-banner">
          <div className="flex items-start gap-3">
            <WarningCircle size={22} weight="fill" className="text-[#FF3333] flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-head text-sm font-bold uppercase tracking-wider text-[#FF3333]">
                Not Listed on Amazon
              </div>
              <div className="text-xs font-mono text-gray-600 mt-1 leading-relaxed">
                Live Keepa search couldn't find this set on Amazon US. The Amazon-side numbers below
                are <span className="text-[#E3000B]">demo placeholders</span> — don't size your buy
                decision on them. BrickLink data is still real.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-[10px] font-mono uppercase tracking-wider text-gray-500">Show demo data</span>
            <Switch checked={showDemoData} onCheckedChange={setShowDemoData}
              className="data-[state=checked]:bg-[#E3000B]" data-testid="not-on-amazon-toggle" />
          </div>
        </div>
      )}

      {/* Economics — Deals-style: click a price to verify it, pencil to pin the true value */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border border-[#E4E4E0] rounded-sm" data-testid="detail-price-cards">
        <PriceCard label="Amazon Buy Box" value={buyBox} hide={notOnAmazon && !showDemoData && mAmz == null}
          href={amzHref} linkTitle={o.asin ? `Open ${o.asin} on Amazon` : "Search on Amazon"} color="text-[#E3000B]"
          manual={mAmz != null} fresh={o.amazon_source === "live"} onEdit={() => openManualPrice("amz_buy_box", buyBox, mAmz != null, s.manual_amz_buy_box_note)} testId="detail-card-amz" />
        <PriceCard label="BL Lowest (US, Sealed)" value={blLowest} href={blHref} linkTitle={`Verify on BrickLink · ${bl.seller_count || 0} US sellers`} color="text-[#0055BF]"
          manual={mBl != null} fresh={o.bricklink_source === "live"} onEdit={() => openManualPrice("bl_sealed", blLowest, mBl != null, s.manual_bl_note)} testId="detail-card-bl" />
        <PriceCard label="BL Avg Sealed" value={blAvg} href={blHref} linkTitle="BL avg — click to verify" color="text-gray-900"
          manual={mBlAvg != null} fresh={o.bricklink_source === "live"} onEdit={() => openManualPrice("bl_avg", blAvg, mBlAvg != null, s.manual_bl_avg_note)} testId="detail-card-bl-avg" />
        <Metric label="Spread $" value={notOnAmazon && !showDemoData ? <span className="text-gray-400 text-sm">--</span> : money((buyBox || 0) - (blLowest || 0))} tone={((buyBox || 0) - (blLowest || 0)) > 0 && !(notOnAmazon && !showDemoData) ? "pos" : "neg"} />
        <Metric label="Net Profit" value={notOnAmazon && !showDemoData ? <span className="text-gray-400 text-sm">--</span> : money(o.net_profit)} tone={o.net_profit >= 0 ? "pos" : "neg"} />
        <Metric label="ROI %" value={notOnAmazon && !showDemoData ? <span className="text-gray-400 text-sm">--</span> : <ROICell value={o.roi_pct} />} />
      </div>

      {/* FBA vs FBM split — hidden when not on Amazon and demo toggled off */}
      {(!notOnAmazon || showDemoData) && (
      <div className="border border-[#E4E4E0] rounded-sm" data-testid="fba-fbm-section">
        <div className="px-4 py-3 border-b border-[#E4E4E0] flex items-center justify-between">
          <span className="font-head font-bold uppercase text-xs tracking-wider text-gray-600">Buy Box · FBA vs FBM</span>
          <span className="font-mono text-[10px] text-gray-500">
            {amz.buy_box_is_fba === true && <span className="text-[#00A850]">FBA WINNING BUY BOX</span>}
            {amz.buy_box_is_fba === false && <span className="text-[#E3000B]">FBM WINNING BUY BOX</span>}
            {amz.buy_box_is_fba == null && <span className="text-gray-400">WINNER UNDETERMINED</span>}
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2">
          <ChannelCard
            label="FBA"
            sublabel="Lowest Fulfilled-by-Amazon offer"
            price={amz.fba_buy_box}
            isWinner={amz.buy_box_is_fba === true}
            buyBox={amz.buy_box}
            tone="accent"
            testId="channel-fba"
          />
          <ChannelCard
            label="FBM"
            sublabel="Lowest Fulfilled-by-Merchant offer"
            price={amz.fbm_buy_box}
            isWinner={amz.buy_box_is_fba === false}
            buyBox={amz.buy_box}
            tone="warn"
            testId="channel-fbm"
            border
          />
        </div>
      </div>
      )}

      {/* Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {(!notOnAmazon || showDemoData) ? (
        <div className="border border-[#E4E4E0] rounded-sm">
          <div className="px-4 py-3 border-b border-[#E4E4E0] font-head font-bold uppercase text-xs tracking-wider text-gray-600">Amazon Breakdown</div>
          <div className="p-4 space-y-2 font-mono text-xs">
            <Row label="Buy Box" value={<PriceLink href={amzHref} value={amz.buy_box} color="text-[#E3000B]" testId="detail-row-buybox" />} />
            <Row label="FBA Lowest" value={<PriceLink href={amzHref} value={amz.fba_buy_box} color="text-[#D97706]" />} />
            <Row label="FBM Lowest" value={<PriceLink href={amzHref} value={amz.fbm_buy_box} color="text-[#E3000B]" />} />
            <Row label="Sales Rank" value={(amz.sales_rank || 0).toLocaleString()} />
            <Row label="Active Offers" value={amz.offer_count ?? 0} />
            <Row label="In Stock" value={amz.in_stock ? <span className="text-[#00A850]">YES</span> : <span className="text-[#FF3333]">NO</span>} />
            <Row label="Est. Monthly Sales" value={amz.est_monthly_sales ?? "--"} />
            <Row label="90d Avg" value={money(amz.avg_90)} />
            <Row label="180d Avg" value={money(amz.avg_180)} />
            <Row label="Hazmat" value={amz.hazmat ? "YES" : "NO"} />
            <Row label="Source" value={o.amazon_source === "live" ? <span className="text-[#00A850]">LIVE (Keepa)</span> : <span className="text-gray-500">DEMO</span>} />
          </div>
        </div>
        ) : (
          <div className="border border-[#E4E4E0] rounded-sm flex items-center justify-center min-h-[280px]" data-testid="amazon-not-listed-card">
            <div className="text-center px-6 py-8">
              <WarningCircle size={32} weight="duotone" className="text-[#FF3333] mx-auto mb-3" />
              <div className="font-head font-bold uppercase text-sm tracking-wider text-gray-800 mb-1">Not on Amazon</div>
              <div className="text-xs font-mono text-gray-500 leading-relaxed max-w-[280px]">
                No Amazon listing was found for this set. Toggle "Show demo data" above
                to view placeholder values, or use BrickLink data only.
              </div>
            </div>
          </div>
        )}

        <div className="border border-[#E4E4E0] rounded-sm">
          <div className="px-4 py-3 border-b border-[#E4E4E0] font-head font-bold uppercase text-xs tracking-wider text-gray-600 flex items-center justify-between">
            <span>BrickLink Breakdown</span>
            <span className="text-[10px] text-[#0055BF] normal-case tracking-normal" data-testid="bl-filter-pill">US sellers · sealed only</span>
          </div>
          <div className="p-4 space-y-2 font-mono text-xs">
            <Row label="Lowest Sealed (US)" value={<PriceLink href={blHref} value={bl.lowest_sealed_us} color="text-[#0055BF]" testId="detail-row-bl-lowest" />} />
            <Row label="Average Sealed" value={<PriceLink href={blHref} value={bl.avg_sealed} color="text-gray-900" />} />
            <Row label="Avg Sold Price (6mo)" value={<PriceLink href={bricklinkPriceGuideUrl(o.set_number)} value={bl.sold_avg} color="text-gray-900" />} />
            <Row label="Qty Available" value={bl.qty_available ?? 0} />
            <Row label="US Sellers" value={bl.seller_count ?? 0} />
            <Row label="Source" value={o.bricklink_source === "live" ? <span className="text-[#00A850]">LIVE (US filtered)</span> : <span className="text-gray-500">DEMO</span>} />
          </div>
        </div>
      </div>

      {/* eBay card — primary sourcing signal (SerpApi) */}
      <EbayCard ebay={o.ebay || {}} setNumber={o.set_number} />

      {/* Fee breakdown */}
      <div className="border border-[#E4E4E0] rounded-sm">
        <div className="px-4 py-3 border-b border-[#E4E4E0] font-head font-bold uppercase text-xs tracking-wider text-gray-600">Economics</div>
        <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-4 font-mono text-xs">
          <Row label="Cost Basis (BL)" value={money(o.cost_basis)} />
          <Row label="Sell Price (AMZ)" value={money(o.sell_price)} />
          <Row label="Amazon Fees" value={money(o.amazon_fees)} />
          <Row label="Shipping" value={money(o.shipping_cost)} />
          <Row label="Prep" value={money(o.prep_cost)} />
          <Row label="Velocity Score" value={(o.velocity_score || 0).toFixed(1)} />
        </div>
      </div>

      {/* Price history chart */}
      <div className="border border-[#E4E4E0] rounded-sm">
        <div className="px-4 py-3 border-b border-[#E4E4E0] font-head font-bold uppercase text-xs tracking-wider text-gray-600">30-Day Price Trend</div>
        <div className="p-4" style={{ height: 280 }} data-testid="price-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={priceHist}>
              <CartesianGrid strokeDasharray="2 2" stroke="#E4E4E0" />
              <XAxis dataKey="i" stroke="#52525B" fontSize={10} />
              <YAxis stroke="#52525B" fontSize={10} />
              <Tooltip contentStyle={{ background: "#F6F6F3", border: "1px solid #E4E4E0", fontFamily: "JetBrains Mono", fontSize: 11 }} />
              <Legend wrapperStyle={{ fontSize: 10, fontFamily: "JetBrains Mono" }} />
              <Line type="monotone" dataKey="amazon" stroke="#E3000B" strokeWidth={2} dot={false} name="Amazon" />
              <Line type="monotone" dataKey="bricklink" stroke="#00A850" strokeWidth={2} dot={false} name="BrickLink" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <ManualPriceDialog state={manualState} setState={setManualState} onDataChanged={reload} />

      {/* Watchlist picker */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#E4E4E0] rounded-sm text-gray-900">
          <DialogHeader><DialogTitle className="font-head uppercase">Add to Watchlist</DialogTitle></DialogHeader>
          <div className="space-y-1">
            {watchlists.length === 0 && <div className="text-xs font-mono text-gray-500">No watchlists yet. Create one from the Watchlists page.</div>}
            {watchlists.map(w => (
              <button key={w.id} onClick={() => addToWl(w.id)} className="w-full flex items-center justify-between px-3 py-2 border border-[#E4E4E0] rounded-sm hover:border-[#E3000B] hover:text-[#E3000B] text-xs font-mono text-left" data-testid={`wl-pick-${w.id}`}>
                <span>{w.name}</span>
                <Plus size={12} />
              </button>
            ))}
          </div>
          <DialogFooter />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Metric({ label, value, tone }) {
  const tones = { pos: "text-[#00A850]", neg: "text-[#FF3333]", accent: "text-[#E3000B]" };
  const cls = tones[tone] || "text-gray-900";
  return (
    <div className="border-r border-b border-[#E4E4E0] px-4 py-3 last:border-r-0">
      <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 mb-1">{label}</div>
      <div className={`font-mono text-lg font-bold tracking-tighter ${cls}`}>{value}</div>
    </div>
  );
}

function PriceLink({ href, value, color, testId }) {
  if (value == null) return <span className="text-gray-400">--</span>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testId}
       className={`${color} hover:underline inline-flex items-center gap-1`} title="Click to verify on the source site">
      {money(value)} <ArrowSquareOut size={9} className="opacity-50" />
    </a>
  );
}

/** Deals-style price card: live/manual indicator · clickable price · pencil to pin the true value. */
function PriceCard({ label, value, hide, href, linkTitle, color, manual, fresh, onEdit, testId }) {
  return (
    <div className="p-4 border-r border-b lg:border-b-0 border-[#E4E4E0] last:border-r-0" data-testid={testId}>
      <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500 mb-1">{label}</div>
      <div className="flex items-center gap-2">
        {manual ? (
          <span title="Manual override — you pinned this value" className="inline-flex items-center px-1 py-0.5 border border-[#E3000B]/60 rounded-sm text-[9px] font-bold text-[#E3000B] bg-[#E3000B]/[0.08]" data-testid={`${testId}-manual`}>M</span>
        ) : (
          <span title={fresh ? "Live data" : "Demo / stale data — run Live Refresh"} className={`inline-block w-1.5 h-1.5 rounded-full ${fresh ? "bg-[#00A850]" : "bg-zinc-700"}`} />
        )}
        {hide ? <span className="text-gray-400 text-sm font-head font-black">N/A</span>
          : value != null ? (
            <a href={href} target="_blank" rel="noopener noreferrer" title={linkTitle} data-testid={`${testId}-link`}
               className={`font-head text-xl font-black ${color} hover:underline`}>{money(value)}</a>
          ) : <span className="text-gray-400 font-head text-xl font-black">--</span>}
        <button onClick={onEdit} title="Edit — pin the true value" data-testid={`${testId}-edit`}
          className="text-gray-500 hover:text-[#E3000B]"><PencilSimple size={12} weight="bold" /></button>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between border-b border-[#F6F6F3] pb-1">
      <span className="text-gray-500 uppercase text-[10px] tracking-wider">{label}</span>
      <span className="text-gray-900">{value}</span>
    </div>
  );
}

function ChannelCard({ label, sublabel, price, isWinner, buyBox, tone, testId, border }) {
  const accent = tone === "accent" ? "#D97706" : "#E3000B";
  const moneyFmt = (n) => (n == null ? "--" : `$${Number(n).toFixed(2)}`);
  const delta = price != null && buyBox != null ? price - buyBox : null;
  const empty = price == null;
  return (
    <div
      data-testid={testId}
      className={`p-5 ${border ? "md:border-l border-[#E4E4E0]" : ""} ${isWinner ? "bg-[#E3000B]/[0.04]" : ""}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-head text-2xl font-black tracking-tighter" style={{ color: accent }}>
              {label}
            </span>
            {isWinner && (
              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-[#00A850]/15 text-[#00A850] border border-[#00A850]/30">
                Buy Box
              </span>
            )}
            {empty && (
              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-gray-200 text-gray-600 border border-gray-300">
                No Offers
              </span>
            )}
          </div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500 mt-0.5">
            {sublabel}
          </div>
        </div>
      </div>

      <div className="font-mono text-3xl font-bold tracking-tighter" style={{ color: empty ? "#52525B" : "white" }}>
        {moneyFmt(price)}
      </div>

      {delta != null && Math.abs(delta) >= 0.5 && (
        <div className="mt-1 text-[10px] font-mono">
          {delta > 0 ? (
            <span className="text-[#FF3333]">+${delta.toFixed(2)} above buy box</span>
          ) : (
            <span className="text-[#00A850]">-${Math.abs(delta).toFixed(2)} below buy box</span>
          )}
        </div>
      )}
      {empty && (
        <div className="mt-1 text-[10px] font-mono text-gray-500">
          No active {label} offers tracked. Click <span className="text-[#E3000B]">LIVE REFRESH</span> to re-fetch from Keepa.
        </div>
      )}
    </div>
  );
}



// Last-live age formatting helpers (used by SetDetail header badge).
const HOUR = 1000 * 60 * 60;
const DAY = HOUR * 24;

function formatAge(ms) {
  if (ms == null || ms < 0) return null;
  if (ms < HOUR) {
    const m = Math.max(1, Math.round(ms / 60000));
    return `${m}m ago`;
  }
  if (ms < DAY) {
    return `${Math.round(ms / HOUR)}h ago`;
  }
  if (ms < DAY * 7) {
    return `${Math.round(ms / DAY)}d ago`;
  }
  return `${Math.round(ms / (DAY * 7))}w ago`;
}

function ageTone(ms) {
  if (ms == null) return null;
  if (ms < HOUR * 6) return "fresh";   // green
  if (ms < DAY * 3) return "warn";     // yellow
  return "stale";                       // orange
}


function EbayCard({ ebay, setNumber }) {
  const has = ebay && ebay.lowest_new != null;
  const searchUrl = `https://www.ebay.com/sch/i.html?_nkw=LEGO+${encodeURIComponent(String(setNumber || "").split("-")[0])}&_sop=15`;
  return (
    <div className="border border-[#0064D2]/40 rounded-sm bg-[#0064D2]/[0.04]" data-testid="detail-ebay-card">
      <div className="px-4 py-3 border-b border-[#E4E4E0] font-head font-bold uppercase text-xs tracking-wider text-gray-800 flex items-center justify-between">
        <span className="flex items-center gap-2">
          <span className="text-[#0064D2]">EBAY</span>
          <span className="text-gray-500 normal-case tracking-normal text-[10px]">Brand New sealed · US sellers · lowest total (price + ship)</span>
        </span>
        <a href={searchUrl} target="_blank" rel="noopener noreferrer"
           className="text-[10px] font-mono text-[#0064D2] hover:underline">
          Open eBay search →
        </a>
      </div>
      <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-6">
        {has ? (
          <>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500">Lowest New (US)</div>
              <div className="font-head text-3xl font-black text-[#0064D2] mt-1" data-testid="detail-ebay-lowest">
                ${Number(ebay.lowest_new).toFixed(2)}
              </div>
              <div className="text-[10px] font-mono text-gray-500 mt-1">
                {ebay.result_count} matching listings after filters
              </div>
            </div>
            <div className="md:col-span-2">
              <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500">Best Listing</div>
              <a href={ebay.listing_url || "#"} target="_blank" rel="noopener noreferrer"
                 className="font-mono text-xs text-gray-900 hover:text-[#0064D2] block mt-1 leading-relaxed"
                 title={ebay.listing_title || ""}>
                {ebay.listing_title || "—"}
              </a>
              <div className="flex items-center gap-4 mt-2 text-[10px] font-mono text-gray-500">
                <span>Condition: <span className="text-gray-800">{ebay.condition || "—"}</span></span>
                {ebay.seller_location && <span>Location: <span className="text-gray-800">{ebay.seller_location}</span></span>}
              </div>
            </div>
          </>
        ) : (
          <div className="md:col-span-3 text-center py-4">
            <div className="font-head text-sm font-bold uppercase text-gray-500 mb-1">No eBay data yet</div>
            <div className="text-[10px] font-mono text-gray-400 leading-relaxed max-w-[400px] mx-auto">
              Hit <span className="text-[#E3000B]">LIVE REFRESH</span> to fetch the current lowest sealed
              listing on eBay via SerpApi. Also populated automatically by the
              PREV scan on the Scan Engine page.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
